import { useEffect, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ApiError } from "../api/client";
import { registrationApi } from "../api/registration";
import { useAuth } from "../auth/AuthContext";
import { Field, ProblemAlert } from "../components/ui";

/** Opened from the reset email (?token=…): choose a new password. Signs out every earlier session. */
export function ResetPasswordPage() {
  const { status, logout, refresh } = useAuth();
  const [params, setParams] = useSearchParams();
  // Read once, then dropped from the address bar so the live token doesn't stay in browser history.
  const [token, setLinkToken] = useState(() => params.get("token"));
  useEffect(() => {
    const fromLink = params.get("token");
    if (fromLink) {
      setLinkToken(fromLink);
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  const [values, setValues] = useState({ newPassword: "", confirm: "" });
  const [mismatch, setMismatch] = useState(false);
  const reset = useMutation({
    mutationFn: () => registrationApi.reset(token ?? "", values.newPassword),
    // If this browser was signed in to the account just reset, that session is now revoked: drop it. A session for a
    // different account still checks out and is kept.
    onSuccess: () => {
      if (status === "authenticated")
        void refresh().catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 401)
            void logout().catch(() => undefined);
        });
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setMismatch(values.newPassword !== values.confirm);
    if (values.newPassword === values.confirm) reset.mutate();
  }

  return (
    <main className="login">
      <section className="login__card" aria-labelledby="reset-title">
        <p className="brand">RegiGov</p>
        <h1 id="reset-title">Choose a new password</h1>
        {!token ? (
          <p className="muted">
            This page needs the link from your reset email.{" "}
            <Link to="/forgot-password">Ask for a new link</Link>.
          </p>
        ) : reset.isSuccess ? (
          <>
            <p role="status" className="notice">
              Your password has been changed and any other sessions have been
              signed out.
            </p>
            <p>
              <Link to="/login">Sign in with your new password</Link>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} className="form">
            <Field label="New password" hint="At least 12 characters">
              <input
                name="newPassword"
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={values.newPassword}
                onChange={(e) =>
                  setValues((v) => ({ ...v, newPassword: e.target.value }))
                }
                required
                disabled={reset.isPending}
              />
            </Field>
            <Field label="Confirm new password">
              <input
                name="confirm"
                type="password"
                autoComplete="new-password"
                value={values.confirm}
                onChange={(e) =>
                  setValues((v) => ({ ...v, confirm: e.target.value }))
                }
                required
                disabled={reset.isPending}
              />
            </Field>
            {mismatch && (
              <p role="alert" className="error">
                The passwords do not match
              </p>
            )}
            <ProblemAlert error={reset.error} />
            {reset.isError && (
              <p>
                <Link to="/forgot-password">Ask for a new link</Link>
              </p>
            )}
            <button type="submit" disabled={reset.isPending}>
              {reset.isPending ? "Saving…" : "Change password"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
